import { PageContainer } from 'src/component/page-container/page-container'
import { Button } from '../../component/ui/button'
import { PageTitle } from '../../component/ui/page-title'
import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useJoinGroupByCode } from 'src/hook/use-groups'
import { isFeatureFlagged } from 'src/util/feature-flags'
import { toasterUtil } from 'src/util/toaster-util'

export const JoinGroupScreen = (): React.ReactElement => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const inviteCode = searchParams.get('code')
  const joinGroupMutation = useJoinGroupByCode()
  const [hasAttempted, setHasAttempted] = useState(false)

  useEffect(() => {
    if (isFeatureFlagged('GROUPS')) {
      navigate('/', { replace: true })
    }
  }, [navigate])

  const handleJoinGroup = useCallback(async (): Promise<void> => {
    if (!inviteCode) {
      toasterUtil.error('No invite code provided')
      return
    }

    try {
      const result = await joinGroupMutation.mutateAsync(inviteCode)
      toasterUtil.success('Successfully joined group!')

      if (result.groupId) {
        navigate(`/groups/${result.groupId}`)
      } else {
        navigate('/groups')
      }
    } catch (error: unknown) {
      const errorMessage =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to join group. The invite code may be invalid or expired.'
      toasterUtil.error(errorMessage)
    }
  }, [inviteCode, joinGroupMutation, navigate])

  useEffect(() => {
    if (inviteCode && !hasAttempted && !joinGroupMutation.isLoading) {
      setHasAttempted(true)
      handleJoinGroup()
    }
  }, [inviteCode, hasAttempted, joinGroupMutation.isLoading, handleJoinGroup])

  if (!inviteCode) {
    return (
      <PageContainer>
        <div className="py-6 md:py-[82px]">
          <div className="text-center py-20">
            <PageTitle>Join Group</PageTitle>
            <p className="text-gray-400 mt-4">No invite code provided. Please use a valid invite link.</p>
            <div className="mt-6">
              <Button onClick={() => navigate('/groups')}>Go to My Groups</Button>
            </div>
          </div>
        </div>
      </PageContainer>
    )
  }

  if (joinGroupMutation.isLoading) {
    return (
      <PageContainer>
        <div className="py-6 md:py-[82px]">
          <div className="text-center py-20">
            <PageTitle>Joining Group...</PageTitle>
            <div className="mt-8 flex justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ivory"></div>
            </div>
          </div>
        </div>
      </PageContainer>
    )
  }

  if (joinGroupMutation.isError) {
    return (
      <PageContainer>
        <div className="py-6 md:py-[82px]">
          <div className="text-center py-20">
            <PageTitle>Failed to Join Group</PageTitle>
            <p className="text-gray-400 mt-4">The invite code may be invalid or expired.</p>
            <div className="mt-6 space-x-4">
              <Button onClick={handleJoinGroup}>Try Again</Button>
              <Button variant="secondary" onClick={() => navigate('/groups')}>
                Go to My Groups
              </Button>
            </div>
          </div>
        </div>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <div className="py-6 md:py-[82px]">
        <div className="text-center py-20">
          <PageTitle>Join Group</PageTitle>
          <p className="text-gray-400 mt-4">
            You are about to join a group with invite code: <code className="bg-gray-700 px-2 py-1 rounded">{inviteCode}</code>
          </p>
          <div className="mt-6">
            <Button onClick={handleJoinGroup}>Join Group</Button>
          </div>
        </div>
      </div>
    </PageContainer>
  )
}
